import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  BookOpen, 
  Clock,
  Play,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Award,
  FileText,
  Video,
  ArrowLeft
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to get YouTube embed URL
const getYouTubeEmbedUrl = (url) => {
  if (!url) return '';
  const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return videoId ? `https://www.youtube.com/embed/${videoId[1]}` : url;
};

// Helper function to get Vimeo embed URL
const getVimeoEmbedUrl = (url) => {
  if (!url) return '';
  const videoId = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return videoId ? `https://player.vimeo.com/video/${videoId[1]}` : url;
};

const CoursePlayer = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [completedLessons, setCompletedLessons] = useState(new Set());
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(null);

  useEffect(() => {
    fetchCourse();
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}`, { withCredentials: true });
      setCourse(response.data);
    } catch (error) {
      console.error("Error fetching course:", error);
      toast.error("Failed to load course");
      navigate('/courses');
    } finally {
      setLoading(false);
    }
  };

  const handleLessonComplete = () => {
    const newCompleted = new Set(completedLessons);
    newCompleted.add(currentLessonIndex);
    setCompletedLessons(newCompleted);
    
    if (currentLessonIndex < (course?.lessons?.length || 0) - 1) {
      setCurrentLessonIndex(currentLessonIndex + 1);
    } else if (course?.quiz_questions?.length > 0) {
      setShowQuiz(true);
    }
    
    toast.success("Lesson completed!");
  };

  const handleQuizSubmit = () => {
    if (!course?.quiz_questions) return;

    let correct = 0;
    course.quiz_questions.forEach((q, i) => {
      if (quizAnswers[i] === q.correct) {
        correct++;
      }
    });

    const score = Math.round((correct / course.quiz_questions.length) * 100);
    setQuizScore(score);
    setQuizSubmitted(true);
    
    if (score >= 70) {
      toast.success(`Congratulations! You scored ${score}%`);
    } else {
      toast.error(`You scored ${score}%. A passing score is 70%.`);
    }
  };

  const progress = course?.lessons?.length 
    ? Math.round((completedLessons.size / course.lessons.length) * 100) 
    : 0;

  const currentLesson = course?.lessons?.[currentLessonIndex];

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!course) {
    return null;
  }

  return (
    <div className="min-h-screen bg-secondary" data-testid="course-player">
      {/* Header */}
      <header className="bg-primary text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/courses">
                <Button variant="ghost" size="icon" className="text-white hover:bg-slate-800" data-testid="back-btn">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div>
                <h1 className="font-heading font-semibold text-lg">{course.title}</h1>
                <p className="text-sm text-slate-300">
                  {course.lessons?.length || 0} lessons • {course.duration_minutes} min
                </p>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-slate-300">Progress</p>
                <p className="font-semibold">{progress}%</p>
              </div>
              <div className="w-32">
                <Progress value={progress} className="h-2 bg-slate-700" />
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-8">
            {!showQuiz ? (
              <Card>
                <CardContent className="p-8">
                  {currentLesson ? (
                    <>
                      <div className="flex items-center gap-3 mb-6">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          currentLesson.type === 'video' ? 'bg-blue-100' : 'bg-amber-100'
                        }`}>
                          {currentLesson.type === 'video' ? (
                            <Video className="w-5 h-5 text-blue-600" />
                          ) : (
                            <FileText className="w-5 h-5 text-amber-600" />
                          )}
                        </div>
                        <div>
                          <Badge variant="secondary" className="mb-1">
                            Lesson {currentLessonIndex + 1} of {course.lessons.length}
                          </Badge>
                          <h2 className="font-heading text-xl font-semibold text-primary">
                            {currentLesson.title}
                          </h2>
                        </div>
                      </div>

                      {/* Lesson Content */}
                      <div className="mb-8">
                        {currentLesson.type === 'video' ? (
                          <div className="bg-slate-900 rounded-xl aspect-video overflow-hidden">
                            {currentLesson.content?.includes('youtube.com') || currentLesson.content?.includes('youtu.be') ? (
                              <iframe
                                src={getYouTubeEmbedUrl(currentLesson.content)}
                                className="w-full h-full"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                title={currentLesson.title}
                              />
                            ) : currentLesson.content?.includes('vimeo.com') ? (
                              <iframe
                                src={getVimeoEmbedUrl(currentLesson.content)}
                                className="w-full h-full"
                                allow="autoplay; fullscreen; picture-in-picture"
                                allowFullScreen
                                title={currentLesson.title}
                              />
                            ) : currentLesson.content?.match(/\.(mp4|webm|ogg)$/i) ? (
                              <video controls className="w-full h-full">
                                <source src={currentLesson.content} type={`video/${currentLesson.content.split('.').pop()}`} />
                                Your browser does not support the video tag.
                              </video>
                            ) : (
                              <div className="flex items-center justify-center h-full">
                                <div className="text-center text-white">
                                  <Video className="w-16 h-16 mx-auto mb-4 opacity-50" />
                                  <p className="text-sm opacity-75">Video URL: {currentLesson.content || 'Not provided'}</p>
                                  <p className="text-xs opacity-50 mt-2">Duration: {currentLesson.duration || currentLesson.duration_minutes} min</p>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="bg-white border border-slate-200 rounded-xl p-6">
                            <div className="prose prose-slate max-w-none">
                              {currentLesson.content ? (
                                currentLesson.content.split('\n').map((paragraph, i) => (
                                  <p key={i} className="mb-4 last:mb-0 text-slate-700 leading-relaxed">
                                    {paragraph}
                                  </p>
                                ))
                              ) : (
                                <div className="text-center py-8">
                                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                  <p className="text-muted-foreground">No content available for this lesson.</p>
                                </div>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-4 pt-4 border-t">
                              Estimated reading time: {currentLesson.duration || currentLesson.duration_minutes} min
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        <Button
                          variant="outline"
                          onClick={() => setCurrentLessonIndex(Math.max(0, currentLessonIndex - 1))}
                          disabled={currentLessonIndex === 0}
                          data-testid="prev-lesson-btn"
                        >
                          <ChevronLeft className="w-4 h-4 mr-2" />
                          Previous
                        </Button>
                        <Button onClick={handleLessonComplete} data-testid="complete-lesson-btn">
                          {completedLessons.has(currentLessonIndex) ? (
                            <>
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Completed
                            </>
                          ) : currentLessonIndex === course.lessons.length - 1 ? (
                            course.quiz_questions?.length > 0 ? (
                              <>
                                Complete & Take Quiz
                                <ChevronRight className="w-4 h-4 ml-2" />
                              </>
                            ) : (
                              <>
                                Complete Course
                                <CheckCircle2 className="w-4 h-4 ml-2" />
                              </>
                            )
                          ) : (
                            <>
                              Mark Complete & Continue
                              <ChevronRight className="w-4 h-4 ml-2" />
                            </>
                          )}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No lessons available for this course.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              /* Quiz Section */
              <Card>
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                      <Award className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <h2 className="font-heading text-xl font-semibold text-primary">
                        Course Quiz
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        {course.quiz_questions?.length || 0} questions • 70% to pass
                      </p>
                    </div>
                  </div>

                  {quizSubmitted ? (
                    <div className="text-center py-8">
                      <div className={`w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center ${
                        quizScore >= 70 ? 'bg-success/10' : 'bg-destructive/10'
                      }`}>
                        {quizScore >= 70 ? (
                          <CheckCircle2 className="w-10 h-10 text-success" />
                        ) : (
                          <Award className="w-10 h-10 text-destructive" />
                        )}
                      </div>
                      <h3 className="font-heading text-2xl font-bold text-primary mb-2">
                        {quizScore >= 70 ? 'Congratulations!' : 'Keep Learning'}
                      </h3>
                      <p className="text-3xl font-bold text-primary mb-4">{quizScore}%</p>
                      <p className="text-muted-foreground mb-8">
                        {quizScore >= 70 
                          ? "You've successfully completed this course!" 
                          : "You need 70% to pass. Review the material and try again."}
                      </p>
                      <div className="flex gap-4 justify-center">
                        <Link to="/courses">
                          <Button variant="outline" data-testid="back-to-courses-btn">Back to Courses</Button>
                        </Link>
                        {quizScore < 70 && (
                          <Button onClick={() => {
                            setQuizSubmitted(false);
                            setQuizAnswers({});
                            setQuizScore(null);
                          }} data-testid="retry-quiz-btn">
                            Retry Quiz
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-8">
                        {course.quiz_questions?.map((question, qIndex) => (
                          <div key={qIndex} className="border border-slate-200 rounded-lg p-6" data-testid={`quiz-question-${qIndex}`}>
                            <p className="font-medium text-primary mb-4">
                              {qIndex + 1}. {question.question}
                            </p>
                            <div className="space-y-2">
                              {question.options?.map((option, oIndex) => (
                                <button
                                  key={oIndex}
                                  onClick={() => setQuizAnswers({...quizAnswers, [qIndex]: oIndex})}
                                  className={`quiz-option w-full text-left ${
                                    quizAnswers[qIndex] === oIndex ? 'selected' : ''
                                  }`}
                                  data-testid={`quiz-option-${qIndex}-${oIndex}`}
                                >
                                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                    quizAnswers[qIndex] === oIndex 
                                      ? 'border-accent bg-accent' 
                                      : 'border-slate-300'
                                  }`}>
                                    {quizAnswers[qIndex] === oIndex && (
                                      <div className="w-2 h-2 rounded-full bg-white" />
                                    )}
                                  </div>
                                  <span>{option}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="mt-8 flex justify-end">
                        <Button 
                          onClick={handleQuizSubmit}
                          disabled={Object.keys(quizAnswers).length < (course.quiz_questions?.length || 0)}
                          data-testid="submit-quiz-btn"
                        >
                          Submit Quiz
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Lesson List */}
          <div className="lg:col-span-4">
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <h3 className="font-heading font-semibold text-primary mb-4">Course Content</h3>
                <div className="space-y-2">
                  {course.lessons?.map((lesson, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setShowQuiz(false);
                        setCurrentLessonIndex(index);
                      }}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                        currentLessonIndex === index && !showQuiz
                          ? 'bg-accent/10 border border-accent'
                          : 'hover:bg-slate-100'
                      }`}
                      data-testid={`lesson-nav-${index}`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        completedLessons.has(index)
                          ? 'bg-success text-white'
                          : currentLessonIndex === index && !showQuiz
                          ? 'bg-accent text-white'
                          : 'bg-slate-200 text-slate-600'
                      }`}>
                        {completedLessons.has(index) ? (
                          <CheckCircle2 className="w-4 h-4" />
                        ) : (
                          index + 1
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-primary text-sm truncate">{lesson.title}</p>
                        <p className="text-xs text-muted-foreground">{lesson.duration} min</p>
                      </div>
                    </button>
                  ))}
                  
                  {course.quiz_questions?.length > 0 && (
                    <button
                      onClick={() => setShowQuiz(true)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                        showQuiz
                          ? 'bg-accent/10 border border-accent'
                          : 'hover:bg-slate-100'
                      }`}
                      data-testid="quiz-nav"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        quizSubmitted && quizScore >= 70
                          ? 'bg-success text-white'
                          : showQuiz
                          ? 'bg-accent text-white'
                          : 'bg-slate-200 text-slate-600'
                      }`}>
                        <Award className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-primary text-sm">Final Quiz</p>
                        <p className="text-xs text-muted-foreground">{course.quiz_questions.length} questions</p>
                      </div>
                    </button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoursePlayer;
